import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devLog } from '../services/devLog'
import { uploadDocument } from '../services/documentsApi'
import {
  createNegotiation,
  addParty,
  uploadVersion,
  getNegotiation,
  getNegotiationStats,
  getVersionDiff,
  listNegotiations,
  listVersions,
  markReadyToSign,
  unmarkReadyToSign,
  removeParty,
  updateDeadline,
  sendToSigning,
  negotiationAction,
  VersionConflictError,
  // Action Items
  listActionItems,
  createActionItem,
  updateActionItem,
  completeActionItem,
  reopenActionItem,
  deleteActionItem,
  // Issues
  listIssues,
  createIssue,
  updateIssue,
  deleteIssue,
  // Pre-flight & Finalize
  getPreFlight,
  finalizeNegotiation,
} from '../services/negotiationsApi'
import {
  writeNegotiationContext,
  deleteNegotiationContext,
  writeDocumentBinding,
} from './useCustomXmlParts'
import type {
  Negotiation,
  NegotiationDetail,
  NegotiationStats,
  VersionDiff,
  VersionListResponse,
  ActionItem,
  NegotiationIssue,
  PreFlightResult,
  CreateActionItemData,
  UpdateActionItemData,
  CreateIssueData,
  UpdateIssueData,
  FinalizeRequest,
  FinalizeResult,
} from '../services/negotiationsApi'

export interface PartyInput {
  actorId?: string
  email?: string
  label: string
  tagName: string
  role: 'CUSTOMER' | 'COUNTERPARTY' | 'TEAM'
}

/** List negotiations for the current actor */
export function useNegotiationList(params?: {
  enabled?: boolean
  status?: string
  actorId?: string
}) {
  return useQuery<Negotiation[]>({
    queryKey: ['negotiations', params?.status, params?.actorId],
    queryFn: () => listNegotiations({
      status: params?.status,
      actorId: params?.actorId,
    }),
    enabled: params?.enabled ?? true,
    staleTime: 30_000,
  })
}

/** Get a single negotiation by ID */
export function useNegotiationDetail(id: number | undefined, actorId?: string) {
  return useQuery<NegotiationDetail>({
    queryKey: ['negotiations', id, actorId],
    queryFn: () => getNegotiation(id!, actorId),
    enabled: id != null,
    staleTime: 10_000,
  })
}

/**
 * Create a negotiation from the current Word document.
 *
 * Multi-step flow:
 * 1. Upload DOCX → creates draft document
 * 2. Create negotiation with document_id
 * 3. Add each party
 * 4. Embed negotiation context in DOCX (Custom XML Parts)
 * 5. Re-capture document blob (now includes XML parts)
 * 6. Upload v1 with embedded context
 *
 * XML parts are written BEFORE version upload so the file stored on
 * the server contains negotiation_id + version. This allows anyone who
 * downloads the version to auto-detect the negotiation when they open it.
 */
export function useCreateNegotiation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      file: Blob
      filename: string
      title: string
      parties: PartyInput[]
      deadline?: string
      actorId?: string
      getDocumentBlob?: () => Promise<Blob>
    }) => {
      devLog.info('Negotiation flow start', `title="${params.title}", parties=${params.parties.length}, actor=${params.actorId || 'default'}`)

      // Step 1: Upload DOCX
      devLog.info('Step 1/6', 'Uploading DOCX...')
      const doc = await uploadDocument(params.file, params.filename, params.title, params.actorId)
      devLog.info('Step 1/6 OK', `koblink_id=${doc.koblink_id}`)

      // Step 2: Create negotiation
      devLog.info('Step 2/6', 'Creating negotiation...')
      const negotiation = await createNegotiation({
        document_id: doc.koblink_id,
        title: params.title,
        deadline: params.deadline || undefined,
        actor_id: params.actorId,
      })
      devLog.info('Step 2/6 OK', `id=${negotiation.id}, koblink_id=${negotiation.koblink_id}`)

      // Step 3: Add parties
      devLog.info('Step 3/6', `Adding ${params.parties.length} parties...`)
      const addedParties = []
      for (const party of params.parties) {
        if (!(party.actorId || party.email) || !party.label || !party.tagName) continue
        const added = await addParty(negotiation.id, {
          actor_id: party.actorId,
          email: party.email,
          party_label: party.label,
          tag_name: party.tagName,
          role: party.role,
        })
        addedParties.push(added)
        devLog.info('Step 3/6', `Added party: ${added.party_label} (#${added.tag_name})`)
      }

      // Step 4: Embed negotiation context in DOCX before uploading version
      devLog.info('Step 4/6', 'Writing negotiation context to Custom XML Parts...')
      await writeNegotiationContext({
        negotiation_id: negotiation.id,
        current_version: 1,
        title: params.title,
        owner_actor_id: params.actorId,
      })

      try {
        await writeDocumentBinding({
          koblink_id: doc.koblink_id,
          title: params.title,
          status: 'negotiating',
          bound_at: new Date().toISOString(),
          owner_actor_id: params.actorId,
        })
      } catch {
        // Best-effort — negotiation context is the primary mechanism
      }

      // Step 5: Re-capture document blob (now includes embedded XML parts)
      let versionBlob = params.file
      if (params.getDocumentBlob) {
        devLog.info('Step 5/6', 'Re-capturing document with embedded context...')
        versionBlob = await params.getDocumentBlob()
      }

      // Step 6: Upload v1
      devLog.info('Step 6/6', 'Uploading v1...')
      const version = await uploadVersion(negotiation.id, versionBlob, 0)
      devLog.info('Step 6/6 OK', `version=${version.version_number}, hash=${version.file_hash.substring(0, 12)}...`)

      return { negotiation, parties: addedParties, version }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

/**
 * Upload a new version to an existing negotiation ("Send tilbake").
 *
 * Embeds negotiation context (Custom XML Parts) in the DOCX before
 * uploading so the stored file is self-describing. Anyone who downloads
 * this version will auto-detect the negotiation when they open it.
 *
 * Handles VersionConflictError (409) by re-throwing — the component
 * shows the conflict dialog.
 */
export function useUploadVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      negotiationId: number
      expectedVersion: number
      title: string
      actorId?: string
      note?: string
      getDocumentBlob: () => Promise<Blob>
    }) => {
      devLog.info('Send back', `negotiation=${params.negotiationId}, expected_version=${params.expectedVersion}`)

      // Write negotiation context BEFORE capturing — embeds metadata in the file
      const predictedVersion = params.expectedVersion + 1
      devLog.info('Send back', `Embedding context (predicted v${predictedVersion}) in DOCX...`)
      await writeNegotiationContext({
        negotiation_id: params.negotiationId,
        current_version: predictedVersion,
        title: params.title,
        owner_actor_id: params.actorId,
      })

      // Capture document blob with embedded XML parts
      const blob = await params.getDocumentBlob()

      const version = await uploadVersion(
        params.negotiationId,
        blob,
        params.expectedVersion,
        params.note,
      )

      devLog.info('Send back OK', `version=${version.version_number}`)

      // Confirm version matches prediction (should always be true)
      if (version.version_number !== predictedVersion) {
        await writeNegotiationContext({
          negotiation_id: params.negotiationId,
          current_version: version.version_number,
          title: params.title,
          owner_actor_id: params.actorId,
        })
      }

      return version
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

/** Add a party to an active negotiation */
export function useAddParty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      negotiationId: number
      actorId?: string
      email?: string
      partyLabel: string
      tagName: string
      role?: 'CUSTOMER' | 'COUNTERPARTY' | 'TEAM'
    }) => {
      devLog.info('Add party', `negotiation=${params.negotiationId}, label=${params.partyLabel}`)
      const party = await addParty(params.negotiationId, {
        actor_id: params.actorId,
        email: params.email,
        party_label: params.partyLabel,
        tag_name: params.tagName,
        role: params.role,
      })
      devLog.info('Add party OK', `id=${party.id}, tag=#${party.tag_name}`)
      return party
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
    },
  })
}

/** Clear negotiation context from the document (used when negotiation is completed/cancelled) */
export async function clearNegotiationContext() {
  await deleteNegotiationContext()
}

/** Get statistics for a negotiation */
export function useNegotiationStats(negotiationId: number | undefined) {
  return useQuery<NegotiationStats>({
    queryKey: ['negotiation-stats', negotiationId],
    queryFn: () => getNegotiationStats(negotiationId!),
    enabled: negotiationId != null,
    staleTime: 60_000,
  })
}

/** List versions for a negotiation */
export function useVersionList(negotiationId: number | undefined) {
  return useQuery<VersionListResponse>({
    queryKey: ['versions', negotiationId],
    queryFn: () => listVersions(negotiationId!),
    enabled: negotiationId != null,
    staleTime: 15_000,
  })
}

/** Mark current actor as ready to sign */
export function useMarkReady() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number }) => {
      devLog.info('Ready', `Marking ready for negotiation ${params.negotiationId}`)
      return await markReadyToSign(params.negotiationId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
    },
  })
}

/** Unmark current actor as ready to sign */
export function useUnmarkReady() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number }) => {
      devLog.info('Ready', `Unmarking ready for negotiation ${params.negotiationId}`)
      return await unmarkReadyToSign(params.negotiationId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
    },
  })
}

/** Remove a party from the negotiation (owner only) */
export function useRemoveParty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; partyId: number }) => {
      devLog.info('Party', `Removing party ${params.partyId} from negotiation ${params.negotiationId}`)
      return await removeParty(params.negotiationId, params.partyId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
    },
  })
}

/** Update negotiation deadline */
export function useUpdateDeadline(negotiationId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (deadline: string | null) => {
      devLog.info('Deadline', `Updating deadline for negotiation ${negotiationId}`)
      return await updateDeadline(negotiationId, deadline)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
    },
  })
}

/** Cancel a negotiation (owner only) */
export function useCancelNegotiation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number }) => {
      devLog.info('Cancel', `Cancelling negotiation ${params.negotiationId}`)
      return await negotiationAction(params.negotiationId, 'cancel')
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

/** Send negotiation to signing */
export function useSendToSigning() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number }) => {
      devLog.info('Signing', `Sending negotiation ${params.negotiationId} to signing`)
      return await sendToSigning(params.negotiationId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

/** Get diff between two versions */
export function useVersionDiff(
  negotiationId: number | undefined,
  fromVersion: number | undefined,
  toVersion: number | undefined,
) {
  return useQuery<VersionDiff>({
    queryKey: ['version-diff', negotiationId, fromVersion, toVersion],
    queryFn: () => getVersionDiff(negotiationId!, fromVersion!, toVersion!),
    enabled:
      negotiationId != null &&
      fromVersion != null &&
      toVersion != null &&
      fromVersion !== toVersion,
    staleTime: 60_000,
  })
}

// ── Action Items hooks ────────────────────────────────

/** List action items for a negotiation */
export function useActionItems(negotiationId: number | undefined) {
  return useQuery<ActionItem[]>({
    queryKey: ['action-items', negotiationId],
    queryFn: () => listActionItems(negotiationId!),
    enabled: negotiationId != null,
    staleTime: 15_000,
  })
}

/** Create an action item */
export function useCreateActionItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number } & CreateActionItemData) => {
      const { negotiationId, ...data } = params
      return await createActionItem(negotiationId, data)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', params.negotiationId] })
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
    },
  })
}

/** Update an action item (title, priority, due_date, assigned_party, etc.) */
export function useUpdateActionItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; itemId: number } & UpdateActionItemData) => {
      const { negotiationId, itemId, ...data } = params
      return await updateActionItem(negotiationId, itemId, data)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', params.negotiationId] })
    },
  })
}

/** Complete an action item */
export function useCompleteActionItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; itemId: number }) => {
      return await completeActionItem(params.negotiationId, params.itemId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', params.negotiationId] })
    },
  })
}

/** Reopen a completed action item */
export function useReopenActionItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; itemId: number }) => {
      return await reopenActionItem(params.negotiationId, params.itemId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', params.negotiationId] })
    },
  })
}

/** Delete an action item */
export function useDeleteActionItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; itemId: number }) => {
      return await deleteActionItem(params.negotiationId, params.itemId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['action-items', params.negotiationId] })
    },
  })
}

// ── Issues hooks ────────────────────────────────

/** List issues for a negotiation */
export function useIssues(negotiationId: number | undefined) {
  return useQuery<NegotiationIssue[]>({
    queryKey: ['issues', negotiationId],
    queryFn: () => listIssues(negotiationId!),
    enabled: negotiationId != null,
    staleTime: 15_000,
  })
}

/** Create an issue */
export function useCreateIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number } & CreateIssueData) => {
      const { negotiationId, ...data } = params
      return await createIssue(negotiationId, data)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['issues', params.negotiationId] })
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
    },
  })
}

/** Update an issue */
export function useUpdateIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; issueId: number } & UpdateIssueData) => {
      const { negotiationId, issueId, ...data } = params
      return await updateIssue(negotiationId, issueId, data)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['issues', params.negotiationId] })
    },
  })
}

/** Delete an issue */
export function useDeleteIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; issueId: number }) => {
      return await deleteIssue(params.negotiationId, params.issueId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['issues', params.negotiationId] })
    },
  })
}

// ── Pre-flight & Finalize hooks ────────────────────────────────

/** Run pre-flight checks */
export function usePreFlight(negotiationId: number | undefined, enabled = false) {
  return useQuery<PreFlightResult>({
    queryKey: ['pre-flight', negotiationId],
    queryFn: () => getPreFlight(negotiationId!),
    enabled: enabled && negotiationId != null,
    staleTime: 0,
  })
}

/** Finalize negotiation → signing */
export function useFinalizeNegotiation() {
  const queryClient = useQueryClient()

  return useMutation<FinalizeResult, Error, { negotiationId: number } & FinalizeRequest>({
    mutationFn: async (params) => {
      const { negotiationId, ...data } = params
      return await finalizeNegotiation(negotiationId, data)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['negotiations', params.negotiationId] })
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
    },
  })
}

export { VersionConflictError }
